import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../store/EntitlementStore';
import { useTheme } from '../store/ThemeContext';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Package,
  Users,
  CreditCard,
  LogOut,
  Sun,
  Moon,
  Monitor,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Ban,
  Flag,
  Edit,
  X,
  Eye,
  Shield,
  Trash2,
  Plus,
  Mail,
} from 'lucide-react';
import api, { updateTenant, getTenantSubscription, getMFADevices, deleteMFADevice, generateRecoveryCodes, resetUserMFA, resendTenantApprovalEmail } from '../services/api';
import { FeatureFlagsSection } from './FeatureFlagsSection';
import { MFAEnrollmentModal } from '../components/MFAEnrollmentModal';
import { MFARecoveryCodesModal } from '../components/MFARecoveryCodesModal';
import './PlatformAdminPage.css';
import { startLogout } from '../utils/session';

/**
 * Platform Admin Console - Main dashboard for superusers.
 * Replaces Django admin in production.
 */
export const PlatformAdminPage = () => {
  const navigate = useNavigate();
  const { user } = useEntitlements();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is superuser
  const isSuperuser = user?.is_superuser;

  useEffect(() => {
    if (!isSuperuser) {
      setError('Access denied. Platform admin privileges required.');
      setLoading(false);
      return;
    }

    // Check MFA enrollment for superusers
    const checkMFAEnrollment = async () => {
      try {
        const response = await getMFADevices();
        // Handle both paginated (results) and non-paginated (devices array) responses
        const devices = response.data.results || response.data.devices || response.data || [];
        const hasEnabledDevice = Array.isArray(devices) && devices.some(d => d.is_enabled);

        // If superuser and MFA required but not enrolled, redirect to enrollment
        if (!hasEnabledDevice) {
          navigate('/mfa/enroll', { 
            replace: true,
            state: { from: '/platform/admin' }
          });
          return;
        }
      } catch (err) {
        console.error('Failed to check MFA enrollment:', err);
        // Continue if check fails (backend will enforce)
      }
    };

    checkMFAEnrollment();
    fetchDashboardStats();
  }, [isSuperuser, navigate]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/dashboard');
      setDashboardStats(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const { theme, effectiveTheme, toggleTheme } = useTheme();

  // Get theme icon
  const getThemeIcon = () => {
    if (theme === 'light') return Sun;
    if (theme === 'dark') return Moon;
    return Monitor;
  };

  const ThemeIcon = getThemeIcon();

  const sections = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'tenants', label: 'Tenants', icon: Building2 },
    { id: 'access-requests', label: 'Access Requests', icon: FileText },
    { id: 'modules', label: 'Modules', icon: Package },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'feature-flags', label: 'Feature Flags & Entitlements', icon: Flag },
    { id: 'mfa', label: 'MFA Configuration', icon: Shield },
  ];

  if (!isSuperuser) {
    return (
      <div className="platform-admin-page">
        <div className="error-message">
          <h2>Access Denied</h2>
          <p>Platform admin privileges are required to access this console.</p>
          <button onClick={() => navigate('/')}>Return to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-admin-page">
      <div className="platform-admin-layout">
        <aside className="admin-sidebar">
          <div className="admin-sidebar-header">
            <div className="admin-logo-wrap">
              <span className="admin-logo-badge" aria-hidden="true">
                A
              </span>
              <div className="admin-logo-text">
                <h2 className="admin-logo">Admin</h2>
                <p className="admin-subtitle">Platform Console</p>
              </div>
            </div>
          </div>
          <nav className="admin-sidebar-nav">
            <ul className="admin-nav-list">
              {sections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <li key={section.id} className="admin-nav-item">
                    <button
                      className={`admin-nav-link ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => setActiveSection(section.id)}
                    >
                      <span className="admin-nav-icon">
                        <IconComponent size={20} />
                      </span>
                      <span className="admin-nav-label">{section.label}</span>
                      {activeSection === section.id && (
                        <ChevronRight size={16} className="admin-nav-chevron" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="admin-sidebar-footer">
            <button
              className="admin-nav-link"
              onClick={() => navigate('/')}
            >
              <span className="admin-nav-icon">
                <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
              </span>
              <span className="admin-nav-label">Exit Admin</span>
            </button>
          </div>
        </aside>

        <div className="admin-main-content">
          <header className="admin-header">
            <div className="admin-header-left">
              <h1 className="admin-page-title">
                {sections.find(s => s.id === activeSection)?.label || 'Admin Console'}
              </h1>
            </div>
            <div className="admin-header-right">
              <button
                className="btn-theme-toggle"
                onClick={toggleTheme}
                title={`Theme: ${theme === 'auto' ? 'Auto (System)' : theme === 'light' ? 'Light' : 'Dark'}`}
                aria-label="Toggle theme"
              >
                <ThemeIcon size={18} />
              </button>
              {user && (
                <div className="admin-user-menu">
                  <span className="admin-user-email">{user.email}</span>
                  <button
                    className="btn-logout"
                    // See PortalrAppLayout: clearing local storage does not
                    // end a BFF session (MP-MIG-001).
                    onClick={startLogout}
                    title="Logout"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              )}
            </div>
          </header>

          <main className="admin-content">
            {loading && (
              <div className="admin-loading">
                <div className="loading-spinner">Loading...</div>
              </div>
            )}
            {error && (
              <div className="admin-error-banner">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && (
              <>
                {activeSection === 'dashboard' && (
                  <DashboardSection stats={dashboardStats} setActiveSection={setActiveSection} />
                )}
                {activeSection === 'tenants' && (
                  <TenantsSection />
                )}
                {activeSection === 'access-requests' && (
                  <AccessRequestsSection />
                )}
                {activeSection === 'modules' && (
                  <ModulesSection />
                )}
                {activeSection === 'users' && (
                  <UsersSection />
                )}
                {activeSection === 'billing' && (
                  <BillingSection />
                )}
                {activeSection === 'feature-flags' && (
                  <FeatureFlagsSection />
                )}
                {activeSection === 'mfa' && (
                  <MFASection />
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

// Dashboard Section Component
const DashboardSection = ({ stats, setActiveSection }) => {
  if (!stats) {
    return (
      <div className="admin-empty-state">
        <p>Loading dashboard statistics...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-section">
      <div className="admin-section-header">
        <h2>Platform Overview</h2>
        <p className="admin-section-description">Real-time statistics and system health</p>
      </div>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <Building2 size={24} className="admin-stat-icon" />
            <h3>Tenants</h3>
          </div>
          <div className="admin-stat-value">{stats.tenants?.total || 0}</div>
          <div className="admin-stat-details">
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Pending</span>
              <span className="admin-stat-detail-value">{stats.tenants?.pending || 0}</span>
            </div>
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Approved</span>
              <span className="admin-stat-detail-value success">{stats.tenants?.approved || 0}</span>
            </div>
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Suspended</span>
              <span className="admin-stat-detail-value warning">{stats.tenants?.suspended || 0}</span>
            </div>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <FileText size={24} className="admin-stat-icon" />
            <h3>Access Requests</h3>
          </div>
          <div className="admin-stat-value">{stats.access_requests?.total || 0}</div>
          <div className="admin-stat-details">
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Pending Review</span>
              <span className="admin-stat-detail-value warning">{stats.access_requests?.pending || 0}</span>
            </div>
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Approved</span>
              <span className="admin-stat-detail-value success">{stats.access_requests?.approved || 0}</span>
            </div>
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Rejected</span>
              <span className="admin-stat-detail-value danger">{stats.access_requests?.rejected || 0}</span>
            </div>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <Users size={24} className="admin-stat-icon" />
            <h3>Users</h3>
          </div>
          <div className="admin-stat-value">{stats.users?.total || 0}</div>
          <div className="admin-stat-details">
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Active</span>
              <span className="admin-stat-detail-value success">{stats.users?.active || 0}</span>
            </div>
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Superusers</span>
              <span className="admin-stat-detail-value">{stats.users?.superusers || 0}</span>
            </div>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <CreditCard size={24} className="admin-stat-icon" />
            <h3>Subscriptions</h3>
          </div>
          <div className="admin-stat-value">{stats.subscriptions?.total || 0}</div>
          <div className="admin-stat-details">
            <div className="admin-stat-detail-item">
              <span className="admin-stat-detail-label">Active</span>
              <span className="admin-stat-detail-value success">{stats.subscriptions?.active || 0}</span>
            </div>
          </div>
        </div>

        {stats.feature_flags && (
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <Flag size={24} className="admin-stat-icon" />
              <h3>Feature Flags</h3>
            </div>
            <div className="admin-stat-value">{stats.feature_flags?.total || 0}</div>
            <div className="admin-stat-details">
              <div className="admin-stat-detail-item">
                <span className="admin-stat-detail-label">Globally Enabled</span>
                <span className="admin-stat-detail-value success">{stats.feature_flags?.globally_enabled || 0}</span>
              </div>
              <div className="admin-stat-detail-item">
                <span className="admin-stat-detail-label">Account Entitlements</span>
                <span className="admin-stat-detail-value">{stats.feature_flags?.account_entitlements || 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="admin-quick-actions">
        <h3 className="admin-section-subtitle">Quick Actions</h3>
        <div className="admin-quick-actions-grid">
          <button
            className="admin-quick-action-card"
            onClick={() => setActiveSection('feature-flags')}
          >
            <Flag size={24} />
            <span>Manage Feature Flags</span>
            <ChevronRight size={16} />
          </button>
          <button
            className="admin-quick-action-card"
            onClick={() => setActiveSection('tenants')}
          >
            <Building2 size={24} />
            <span>Manage Tenants</span>
            <ChevronRight size={16} />
          </button>
          <button
            className="admin-quick-action-card"
            onClick={() => setActiveSection('access-requests')}
          >
            <FileText size={24} />
            <span>Review Access Requests</span>
            <ChevronRight size={16} />
          </button>
          <button
            className="admin-quick-action-card"
            onClick={() => setActiveSection('modules')}
          >
            <Package size={24} />
            <span>Manage Modules</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Tenant Details Modal Component
const TenantDetailsModal = ({ tenant, isOpen, onClose, onUpdate, onResendApprovalEmail }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (tenant && isOpen) {
      setFormData({
        name: tenant.name || '',
        firm_name: tenant.firm_name || '',
        email: tenant.email || '',
        billing_email: tenant.billing_email || '',
        billing_address_line1: tenant.billing_address_line1 || '',
        billing_address_line2: tenant.billing_address_line2 || '',
        billing_city: tenant.billing_city || '',
        billing_state: tenant.billing_state || '',
        billing_postal_code: tenant.billing_postal_code || '',
        billing_country: tenant.billing_country || 'US',
      });
      setIsEditing(false);
      fetchSubscription();
    }
  }, [tenant, isOpen]);

  const fetchSubscription = async () => {
    if (!tenant) return;
    try {
      const response = await getTenantSubscription(tenant.id);
      setSubscription(response.data);
    } catch (err) {
      // Subscription might not exist, that's okay
      setSubscription(null);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await updateTenant(tenant.id, formData);
      setIsEditing(false);
      onUpdate();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        firm_name: tenant.firm_name || '',
        email: tenant.email || '',
        billing_email: tenant.billing_email || '',
        billing_address_line1: tenant.billing_address_line1 || '',
        billing_address_line2: tenant.billing_address_line2 || '',
        billing_city: tenant.billing_city || '',
        billing_state: tenant.billing_state || '',
        billing_postal_code: tenant.billing_postal_code || '',
        billing_country: tenant.billing_country || 'US',
      });
    }
  };

  if (!isOpen || !tenant) return null;

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>Tenant Details</h3>
          <div className="admin-modal-actions">
            {!isEditing && (
              <>
                <button
                  className="admin-btn admin-btn-primary"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit size={16} />
                  Edit
                </button>
                {tenant.access_status === 'approved' && onResendApprovalEmail && (
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => onResendApprovalEmail(tenant.id)}
                    title="Resend approval email with new temporary password"
                  >
                    <Mail size={16} />
                    Resend Approval Email
                  </button>
                )}
              </>
            )}
            <button
              className="admin-btn admin-btn-secondary"
              onClick={onClose}
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="admin-modal-body">
          <div className="admin-form-grid">
            <div className="admin-form-group">
              <label>Tenant Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.name || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>Firm Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.firm_name}
                  onChange={(e) => setFormData({ ...formData, firm_name: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.firm_name || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.email || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>Billing Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.billing_email}
                  onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_email || '-'}</div>
              )}
            </div>
            <div className="admin-form-group full-width">
              <label>Billing Address Line 1</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.billing_address_line1}
                  onChange={(e) => setFormData({ ...formData, billing_address_line1: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_address_line1 || '-'}</div>
              )}
            </div>
            <div className="admin-form-group full-width">
              <label>Billing Address Line 2</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.billing_address_line2}
                  onChange={(e) => setFormData({ ...formData, billing_address_line2: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_address_line2 || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>City</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.billing_city}
                  onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_city || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>State</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.billing_state}
                  onChange={(e) => setFormData({ ...formData, billing_state: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_state || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>Postal Code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.billing_postal_code}
                  onChange={(e) => setFormData({ ...formData, billing_postal_code: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_postal_code || '-'}</div>
              )}
            </div>
            <div className="admin-form-group">
              <label>Country</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.billing_country}
                  onChange={(e) => setFormData({ ...formData, billing_country: e.target.value })}
                  className="admin-form-input"
                />
              ) : (
                <div className="admin-form-value">{tenant.billing_country || '-'}</div>
              )}
            </div>
            <div className="admin-form-group full-width">
              <label>Status</label>
              <div className="admin-form-value">
                <span className={`admin-status-badge admin-status-${tenant.access_status}`}>
                  {tenant.access_status === 'pending' && <AlertCircle size={12} />}
                  {tenant.access_status === 'approved' && <CheckCircle2 size={12} />}
                  {tenant.access_status === 'rejected' && <XCircle size={12} />}
                  {tenant.access_status === 'suspended' && <Ban size={12} />}
                  <span>{tenant.access_status}</span>
                </span>
              </div>
            </div>
            {subscription && (
              <div className="admin-form-group full-width">
                <label>Current Subscription</label>
                <div className="admin-form-value">
                  <div>
                    <strong>{subscription.plan?.name || 'No Plan'}</strong> - {subscription.status}
                  </div>
                  {subscription.billing_cycle && (
                    <div className="admin-form-subtext">
                      Billing: {subscription.billing_cycle}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="admin-form-group full-width">
              <label>Metrics</label>
              <div className="admin-form-value">
                <div>Expected Clients: {tenant.expected_clients || '-'}</div>
                <div>Actual Clients: {tenant.actual_clients || '-'}</div>
                <div>Expected Staff: {tenant.expected_staff || '-'}</div>
                <div>Actual Staff: {tenant.actual_staff || '-'}</div>
              </div>
            </div>
          </div>
        </div>
        {isEditing && (
          <div className="admin-modal-footer">
            <button
              className="admin-btn admin-btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Tenants Section Component
const TenantsSection = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [filter]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { access_status: filter } : {};
      const response = await api.get('/platform/tenants/', { params });
      setTenants(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (tenantId, action, notes = '') => {
    if (!window.confirm(`Are you sure you want to ${action} this tenant?`)) {
      return;
    }

    try {
      await api.post(`/platform/tenants/${tenantId}/${action}/`, { notes });
      await fetchTenants();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} tenant`);
    }
  };

  const handleResendApprovalEmail = async (tenantId) => {
    if (!window.confirm('Resend the approval/welcome email to this tenant? A new temporary password will be included.')) {
      return;
    }

    try {
      await resendTenantApprovalEmail(tenantId);
      alert('Approval email sent successfully.');
      await fetchTenants();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resend approval email');
    }
  };

  const handleViewDetails = (tenant) => {
    setSelectedTenant(tenant);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedTenant(null);
  };

  const handleTenantUpdate = () => {
    fetchTenants();
  };

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Tenant Management</h2>
          <p className="admin-section-description">Manage tenant access and status</p>
        </div>
        <div className="admin-filters">
          {['all', 'pending', 'approved', 'rejected', 'suspended'].map((status) => (
            <button
              key={status}
              className={`admin-filter-btn ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner">Loading tenants...</div>
        </div>
      ) : tenants.length === 0 ? (
        <div className="admin-empty-state">
          <Building2 size={48} />
          <p>No tenants found</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>
                    <div className="admin-table-cell-primary">
                      {tenant.firm_name || tenant.name}
                    </div>
                  </td>
                  <td>{tenant.email}</td>
                  <td>
                    <span className={`admin-status-badge admin-status-${tenant.access_status}`}>
                      {tenant.access_status === 'pending' && <AlertCircle size={12} />}
                      {tenant.access_status === 'approved' && <CheckCircle2 size={12} />}
                      {tenant.access_status === 'rejected' && <XCircle size={12} />}
                      {tenant.access_status === 'suspended' && <Ban size={12} />}
                      <span>{tenant.access_status}</span>
                    </span>
                  </td>
                  <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="admin-action-buttons">
                      <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => handleViewDetails(tenant)}
                        title="View tenant details"
                      >
                        <Eye size={16} />
                        View Details
                      </button>
                      {tenant.access_status === 'pending' && (
                        <>
                          <button
                            className="admin-btn admin-btn-success"
                            onClick={() => handleAction(tenant.id, 'approve')}
                            title="Approve tenant access"
                          >
                            <CheckCircle2 size={16} />
                            Approve
                          </button>
                          <button
                            className="admin-btn admin-btn-danger"
                            onClick={() => handleAction(tenant.id, 'reject')}
                            title="Reject tenant access"
                          >
                            <XCircle size={16} />
                            Reject
                          </button>
                        </>
                      )}
                      {tenant.access_status === 'approved' && (
                        <>
                          <button
                            className="admin-btn admin-btn-secondary"
                            onClick={() => handleResendApprovalEmail(tenant.id)}
                            title="Resend approval email with new temporary password"
                          >
                            <Mail size={16} />
                            Resend Approval Email
                          </button>
                          <button
                            className="admin-btn admin-btn-warning"
                            onClick={() => handleAction(tenant.id, 'suspend')}
                            title="Suspend tenant access"
                          >
                            <Ban size={16} />
                            Suspend
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TenantDetailsModal
        tenant={selectedTenant}
        isOpen={showModal}
        onClose={handleModalClose}
        onUpdate={handleTenantUpdate}
        onResendApprovalEmail={handleResendApprovalEmail}
      />
    </div>
  );
};

// Access Requests Section
const AccessRequestsSection = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await api.get('/platform/access-requests/', { params });
      setRequests(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load access requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this access request?')) {
      return;
    }
    try {
      setActionLoading(true);
      await api.post(`/platform/access-requests/${requestId}/approve/`, { notes: reviewNotes });
      setSelectedRequest(null);
      setReviewNotes('');
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this access request?')) {
      return;
    }
    try {
      setActionLoading(true);
      await api.post(`/platform/access-requests/${requestId}/reject/`, { notes: reviewNotes });
      setSelectedRequest(null);
      setReviewNotes('');
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Access Requests</h2>
          <p className="admin-section-description">Review and manage tenant access requests</p>
        </div>
        <div className="admin-filters">
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              className={`admin-filter-btn ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner">Loading access requests...</div>
        </div>
      ) : requests.length === 0 ? (
        <div className="admin-empty-state">
          <FileText size={48} />
          <p>No access requests found</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <div className="admin-table-cell-primary">
                      {request.organization_name}
                    </div>
                  </td>
                  <td>{request.primary_contact_name}</td>
                  <td>{request.email}</td>
                  <td>{formatDate(request.created_at)}</td>
                  <td>
                    <span className={`admin-status-badge admin-status-${request.status}`}>
                      {request.status === 'pending' && <AlertCircle size={12} />}
                      {request.status === 'approved' && <CheckCircle2 size={12} />}
                      {request.status === 'rejected' && <XCircle size={12} />}
                      <span>{request.status}</span>
                    </span>
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => {
                        setSelectedRequest(request);
                        setReviewNotes('');
                      }}
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedRequest && (
        <div
          className="admin-modal-overlay"
          onClick={() => setSelectedRequest(null)}
        >
          <div
            className="admin-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-header">
              <h3>Access Request Details</h3>
              <div className="admin-modal-actions">
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => setSelectedRequest(null)}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-grid">
                <div className="admin-form-group full-width">
                  <h4 className="admin-detail-section-title">Organization Information</h4>
                </div>
                <div className="admin-form-group">
                  <label>Organization Name</label>
                  <div className="admin-form-value">{selectedRequest.organization_name}</div>
                </div>
                <div className="admin-form-group">
                  <label>Contact</label>
                  <div className="admin-form-value">{selectedRequest.primary_contact_name}</div>
                </div>
                <div className="admin-form-group">
                  <label>Email</label>
                  <div className="admin-form-value">{selectedRequest.email}</div>
                </div>
                <div className="admin-form-group">
                  <label>Phone</label>
                  <div className="admin-form-value">{selectedRequest.phone || 'Not provided'}</div>
                </div>
              </div>
              <div className="admin-form-grid">
                <div className="admin-form-group full-width">
                  <h4 className="admin-detail-section-title">Request Details</h4>
                </div>
                <div className="admin-form-group full-width">
                  <label>How do you plan to use Portalr?</label>
                  <div className="admin-form-value admin-form-value-textblock">
                    {selectedRequest.intended_use || 'Not provided'}
                  </div>
                </div>
                <div className="admin-form-group">
                  <label>Expected Clients</label>
                  <div className="admin-form-value">
                    {selectedRequest.expected_clients ?? 'Not provided'}
                  </div>
                </div>
                <div className="admin-form-group">
                  <label>Expected Staff</label>
                  <div className="admin-form-value">
                    {selectedRequest.expected_staff ?? 'Not provided'}
                  </div>
                </div>
              </div>
              <div className="admin-form-grid">
                <div className="admin-form-group full-width">
                  <h4 className="admin-detail-section-title">Status</h4>
                </div>
                <div className="admin-form-group">
                  <label>Status</label>
                  <div className="admin-form-value">
                    <span className={`admin-status-badge admin-status-${selectedRequest.status}`}>
                      {selectedRequest.status}
                    </span>
                  </div>
                </div>
                <div className="admin-form-group">
                  <label>Requested</label>
                  <div className="admin-form-value">{formatDateTime(selectedRequest.created_at)}</div>
                </div>
                {selectedRequest.reviewed_at && (
                  <div className="admin-form-group">
                    <label>Reviewed</label>
                    <div className="admin-form-value">
                      {formatDateTime(selectedRequest.reviewed_at)}
                    </div>
                  </div>
                )}
                {selectedRequest.reviewed_by_email && (
                  <div className="admin-form-group">
                    <label>Reviewed By</label>
                    <div className="admin-form-value">{selectedRequest.reviewed_by_email}</div>
                  </div>
                )}
                {selectedRequest.review_notes && (
                  <div className="admin-form-group full-width">
                    <label>Review Notes</label>
                    <div className="admin-form-value admin-form-value-textblock">
                      {selectedRequest.review_notes}
                    </div>
                  </div>
                )}
              </div>
              {selectedRequest.status === 'pending' && (
                <>
                  <div className="admin-form-group full-width" style={{ marginTop: '1rem' }}>
                    <label>Review Notes (optional)</label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add any notes about this decision..."
                      rows={4}
                      className="admin-form-input"
                    />
                  </div>
                  <div className="admin-modal-footer">
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => setSelectedRequest(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={() => handleApprove(selectedRequest.id)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      className="admin-btn admin-btn-danger"
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modules Section Component
const ModulesSection = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/modules/');
      setModules(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (moduleId, enabled) => {
    const action = enabled ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} this module?`)) {
      return;
    }

    try {
      await api.post(`/platform/modules/${moduleId}/${action}/`);
      await fetchModules();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} module`);
    }
  };

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Module Entitlements</h2>
          <p className="admin-section-description">Manage module access for tenants</p>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner">Loading modules...</div>
        </div>
      ) : modules.length === 0 ? (
        <div className="admin-empty-state">
          <Package size={48} />
          <p>No module entitlements found</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Module</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((module) => (
                <tr key={module.id}>
                  <td>
                    <div className="admin-table-cell-primary">
                      {module.tenant_name || module.tenant}
                    </div>
                  </td>
                  <td>
                    <span className="admin-module-name">{module.module}</span>
                  </td>
                  <td>
                    <span className={`admin-status-badge ${module.is_enabled ? 'admin-status-enabled' : 'admin-status-disabled'}`}>
                      {module.is_enabled ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      <span>{module.is_enabled ? 'Enabled' : 'Disabled'}</span>
                    </span>
                  </td>
                  <td>
                    <button
                      className={`admin-btn ${module.is_enabled ? 'admin-btn-warning' : 'admin-btn-success'}`}
                      onClick={() => handleToggle(module.id, module.is_enabled)}
                    >
                      {module.is_enabled ? (
                        <>
                          <Ban size={16} />
                          Disable
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={16} />
                          Enable
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// User Details Modal Component
const UserDetailsModal = ({ user, isOpen, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);

  const handleUserAction = async (action) => {
    if (!user) return;
    const actionLabels = { enable: 'enable', disable: 'disable', promote_superuser: 'promote to superuser', demote_superuser: 'demote from superuser' };
    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} this user?`)) return;
    try {
      setLoading(true);
      await api.post(`/platform/users/${user.id}/${action}/`, {});
      onUpdate();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} user`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>User Details</h3>
          <div className="admin-modal-actions">
            <button className="admin-btn admin-btn-secondary" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="admin-modal-body">
          <div className="admin-form-grid">
            <div className="admin-form-group">
              <label>Email</label>
              <div className="admin-form-value">{user.email}</div>
            </div>
            <div className="admin-form-group">
              <label>Name</label>
              <div className="admin-form-value">{[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}</div>
            </div>
            <div className="admin-form-group">
              <label>Role</label>
              <div className="admin-form-value">{user.role || '-'}</div>
            </div>
            <div className="admin-form-group">
              <label>Tenant</label>
              <div className="admin-form-value">{user.tenant_name || '-'}</div>
            </div>
            <div className="admin-form-group">
              <label>Status</label>
              <div className="admin-form-value">
                <span className={`admin-status-badge ${user.is_active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                  {user.is_active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                  <span>{user.is_active ? 'Active' : 'Inactive'}</span>
                </span>
              </div>
            </div>
            <div className="admin-form-group">
              <label>MFA</label>
              <div className="admin-form-value">
                {user.mfa_enrolled ? (
                  <span className="admin-status-badge admin-status-enabled">Enabled</span>
                ) : (
                  <span className="admin-status-badge admin-status-disabled">Disabled</span>
                )}
              </div>
            </div>
            <div className="admin-form-group">
              <label>Type</label>
              <div className="admin-form-value">
                {user.is_superuser ? (
                  <span className="admin-superuser-badge">Superuser</span>
                ) : (
                  <span className="admin-status-text">Tenant User</span>
                )}
              </div>
            </div>
            <div className="admin-form-group">
              <label>Created</label>
              <div className="admin-form-value">{formatDate(user.created_at)}</div>
            </div>
          </div>
          <div className="admin-modal-footer" style={{ marginTop: '1rem' }}>
            {user.is_active ? (
              <button
                className="admin-btn admin-btn-warning"
                onClick={() => handleUserAction('disable')}
                disabled={loading}
              >
                Disable User
              </button>
            ) : (
              <button
                className="admin-btn admin-btn-success"
                onClick={() => handleUserAction('enable')}
                disabled={loading}
              >
                Enable User
              </button>
            )}
            {!user.is_superuser ? (
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => handleUserAction('promote_superuser')}
                disabled={loading}
              >
                Promote to Superuser
              </button>
            ) : (
              <button
                className="admin-btn admin-btn-warning"
                onClick={() => handleUserAction('demote_superuser')}
                disabled={loading}
              >
                Demote from Superuser
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Users Section Component
const UsersSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'superusers', 'tenants'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/users/');
      setUsers(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (filter === 'superusers') {
      return user.is_superuser === true;
    } else if (filter === 'tenants') {
      return user.is_superuser === false;
    }
    return true; // 'all'
  });

  // Sort by tenant name, superusers first (tenant_name null)
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const nameA = (a.tenant_name || '').toLowerCase();
    const nameB = (b.tenant_name || '').toLowerCase();
    if (!nameA && !nameB) return 0;
    if (!nameA) return -1;
    if (!nameB) return 1;
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Platform Users</h2>
          <p className="admin-section-description">Manage platform user accounts</p>
        </div>
        <div className="admin-filters">
          {['all', 'superusers', 'tenants'].map((filterType) => (
            <button
              key={filterType}
              className={`admin-filter-btn ${filter === filterType ? 'active' : ''}`}
              onClick={() => setFilter(filterType)}
            >
              {filterType === 'all' ? 'All Users' : filterType === 'superusers' ? 'Superusers' : 'Tenant Users'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner">Loading users...</div>
        </div>
      ) : sortedUsers.length === 0 ? (
        <div className="admin-empty-state">
          <Users size={48} />
          <p>No users found</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Tenant</th>
                <th>Status</th>
                <th>MFA</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="admin-table-cell-primary">
                      {user.email}
                    </div>
                  </td>
                  <td>
                    <span className="admin-role-badge">{user.role || '-'}</span>
                  </td>
                  <td>
                    {user.tenant_name || user.tenant || '-'}
                  </td>
                  <td>
                    <span className={`admin-status-badge ${user.is_active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                      {user.is_active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      <span>{user.is_active ? 'Active' : 'Inactive'}</span>
                    </span>
                  </td>
                  <td>
                    {user.mfa_enrolled ? (
                      <span className="admin-status-badge admin-status-enabled" title="MFA Enabled">
                        <Shield size={12} />
                        <span>Enabled</span>
                      </span>
                    ) : (
                      <span className="admin-status-badge admin-status-disabled" title="MFA Not Enabled">
                        <Shield size={12} />
                        <span>Disabled</span>
                      </span>
                    )}
                  </td>
                  <td>
                    {user.is_superuser ? (
                      <span className="admin-superuser-badge">
                        <CheckCircle2 size={12} />
                        Superuser
                      </span>
                    ) : (
                      <span className="admin-status-text">Tenant User</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowUserModal(true);
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <UserDetailsModal
        user={selectedUser}
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        onUpdate={fetchUsers}
      />
    </div>
  );
};

// Billing Section Component
const BillingSection = () => {
  const [tenants, setTenants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'canceled', 'trial'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch all tenants
      const tenantsResponse = await api.get('/platform/tenants/');
      const tenantsList = tenantsResponse.data.results || tenantsResponse.data;
      setTenants(tenantsList);

      // Fetch subscriptions for each tenant
      const subscriptionsList = [];
      for (const tenant of tenantsList) {
        try {
          const subResponse = await getTenantSubscription(tenant.id);
          if (subResponse.data && !subResponse.data.message) {
            subscriptionsList.push({
              ...subResponse.data,
              tenant_name: tenant.firm_name || tenant.name,
              tenant_email: tenant.email,
            });
          }
        } catch (err) {
          // Tenant might not have a subscription, skip
        }
      }
      setSubscriptions(subscriptionsList);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Billing & Subscriptions</h2>
          <p className="admin-section-description">Manage tenant billing and subscriptions</p>
        </div>
        <div className="admin-filters">
          {['all', 'active', 'canceled', 'trial', 'past_due'].map((status) => (
            <button
              key={status}
              className={`admin-filter-btn ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner">Loading subscriptions...</div>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="admin-empty-state">
          <CreditCard size={48} />
          <p>No subscriptions found</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Billing Cycle</th>
                <th>Current Period</th>
                <th>Next Billing</th>
                <th>Stripe ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td>
                    <div className="admin-table-cell-primary">
                      {sub.tenant_name}
                    </div>
                    <div className="admin-table-cell-secondary">
                      {sub.tenant_email}
                    </div>
                  </td>
                  <td>
                    <span className="admin-module-name">
                      {sub.plan?.name || 'No Plan'}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-status-badge admin-status-${sub.status}`}>
                      {sub.status === 'active' && <CheckCircle2 size={12} />}
                      {sub.status === 'canceled' && <XCircle size={12} />}
                      {sub.status === 'trial' && <AlertCircle size={12} />}
                      {sub.status === 'past_due' && <Ban size={12} />}
                      <span>{sub.status}</span>
                    </span>
                  </td>
                  <td>
                    {sub.billing_cycle ? (
                      <span className="admin-status-text">
                        {sub.billing_cycle.charAt(0).toUpperCase() + sub.billing_cycle.slice(1)}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {sub.current_period_start && sub.current_period_end ? (
                      <div>
                        <div>{formatDate(sub.current_period_start)}</div>
                        <div className="admin-table-cell-secondary">to {formatDate(sub.current_period_end)}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {sub.current_period_end ? formatDate(sub.current_period_end) : '-'}
                  </td>
                  <td>
                    {sub.stripe_subscription_id ? (
                      <span className="admin-status-text" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {sub.stripe_subscription_id.substring(0, 20)}...
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// MFA Section Component
const MFASection = () => {
  const [devices, setDevices] = useState([]);
  const [recoveryCodesCount, setRecoveryCodesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showRecoveryCodesModal, setShowRecoveryCodesModal] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  useEffect(() => {
    fetchMFAData();
  }, []);

  const fetchMFAData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMFADevices();
      const data = response.data;
      const deviceList = Array.isArray(data?.devices) ? data.devices
        : Array.isArray(data?.results) ? data.results
        : Array.isArray(data) ? data : [];
      setDevices(deviceList);
    } catch (err) {
      console.error('Failed to load MFA devices:', err);
      setError(err.response?.data?.error || 'Failed to load MFA devices');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to remove this device? You will need to enroll it again to use it for MFA.')) {
      return;
    }

    try {
      await deleteMFADevice(deviceId);
      await fetchMFAData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete device');
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    try {
      const response = await generateRecoveryCodes();
      if (response.data.recovery_codes) {
        setRecoveryCodes(response.data.recovery_codes);
        setShowRecoveryCodesModal(true);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate recovery codes');
    }
  };

  const handleEnrollmentSuccess = () => {
    fetchMFAData();
  };

  const isMFAEnrolled = devices.length > 0;

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Multi-Factor Authentication</h2>
          <p className="admin-section-description">Manage your MFA devices and recovery codes</p>
        </div>
      </div>

      {error && (
        <div className="admin-error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner">Loading MFA configuration...</div>
        </div>
      ) : (
        <>
          <div className="admin-stat-card" style={{ marginBottom: '2rem' }}>
            <div className="admin-stat-header">
              <Shield size={24} className="admin-stat-icon" />
              <h3>MFA Status</h3>
            </div>
            <div className="admin-stat-value">
              {isMFAEnrolled ? (
                <span style={{ color: "var(--color-success)" }}>
                  <CheckCircle2 size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Enabled
                </span>
              ) : (
                <span style={{ color: "var(--color-error)" }}>
                  <XCircle size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Not Enabled
                </span>
              )}
            </div>
            <div className="admin-stat-details">
              <div className="admin-stat-detail-item">
                <span className="admin-stat-detail-label">Devices</span>
                <span className="admin-stat-detail-value">{devices.length}</span>
              </div>
              {recoveryCodesCount > 0 && (
                <div className="admin-stat-detail-item">
                  <span className="admin-stat-detail-label">Recovery Codes</span>
                  <span className="admin-stat-detail-value">{recoveryCodesCount} unused</span>
                </div>
              )}
            </div>
          </div>

          <div className="admin-section-subsection">
            <div className="admin-subsection-header">
              <h3>MFA Devices</h3>
              <button
                className="admin-btn admin-btn-primary"
                onClick={() => setShowEnrollmentModal(true)}
              >
                <Plus size={16} />
                Add Device
              </button>
            </div>

            {devices.length === 0 ? (
              <div className="admin-empty-state">
                <Shield size={48} />
                <p>No MFA devices enrolled</p>
                <p style={{ fontSize: '0.9rem', color: "var(--color-text-secondary)", marginTop: '0.5rem' }}>
                  Add a device to enable multi-factor authentication
                </p>
              </div>
            ) : (
              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Device Name</th>
                      <th>Status</th>
                      <th>Primary</th>
                      <th>Last Used</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device) => (
                      <tr key={device.id}>
                        <td>
                          <div className="admin-table-cell-primary">
                            {device.name}
                          </div>
                        </td>
                        <td>
                          <span className={`admin-status-badge ${device.is_enabled ? 'admin-status-enabled' : 'admin-status-disabled'}`}>
                            {device.is_enabled ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                            <span>{device.is_enabled ? 'Enabled' : 'Disabled'}</span>
                          </span>
                        </td>
                        <td>
                          {device.is_primary ? (
                            <span className="admin-status-badge admin-status-enabled">
                              <CheckCircle2 size={12} />
                              Primary
                            </span>
                          ) : (
                            <span className="admin-status-text">-</span>
                          )}
                        </td>
                        <td>
                          {device.last_used_at
                            ? new Date(device.last_used_at).toLocaleString()
                            : 'Never'}
                        </td>
                        <td>
                          <button
                            className="admin-btn admin-btn-danger"
                            onClick={() => handleDeleteDevice(device.id)}
                            disabled={device.is_primary && devices.length === 1}
                            title={device.is_primary && devices.length === 1 ? 'Cannot delete primary device' : 'Remove device'}
                          >
                            <Trash2 size={16} />
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="admin-section-subsection" style={{ marginTop: '2rem' }}>
            <div className="admin-subsection-header">
              <h3>Recovery Codes</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {recoveryCodesCount > 0 && (
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => {
                      setShowRecoveryCodesModal(true);
                    }}
                  >
                    View Codes
                  </button>
                )}
                <button
                  className="admin-btn admin-btn-primary"
                  onClick={handleGenerateRecoveryCodes}
                >
                  Generate New Codes
                </button>
              </div>
            </div>
            <p style={{ color: "var(--color-text-secondary)", marginTop: '0.5rem' }}>
              Recovery codes can be used to access your account if you lose access to your authenticator device.
              Each code can only be used once.
            </p>
          </div>
        </>
      )}

      <MFAEnrollmentModal
        isOpen={showEnrollmentModal}
        onClose={() => setShowEnrollmentModal(false)}
        onSuccess={handleEnrollmentSuccess}
      />

      <MFARecoveryCodesModal
        isOpen={showRecoveryCodesModal}
        onClose={() => {
          setShowRecoveryCodesModal(false);
          setRecoveryCodes(null);
        }}
        recoveryCodes={recoveryCodes}
        onRegenerate={handleGenerateRecoveryCodes}
      />
    </div>
  );
};
